import { Connection, PublicKey } from "@solana/web3.js";
import { useEffect, useState } from "react";
import { useAppSelector } from "@axvdex/state";
import { selectWalletInfo } from "@axvdex/state/wallet/walletSelectors";
import { Bidder, Sale } from "@axvdex/types/solana/outbid";
import { getSale, useGetBidderInfo } from "./useOutbid";
import { findBidder, findSoldOutBidderIncentives, findSpecificBidsIncentives } from "./utils/DeriveOutbidPDA";

/*
 * Hook to subscribe to bidder info related to a sale.
 * @param initialSaleAccount - The sale account to subscribe to
 * @returns userBidderInfo - The global bid info of the user
 * @returns setSaleAccount - Function to set the sale account
 */
export function useSubscribeToBidderInfo(initialSaleAccount: PublicKey | null): {
  bidderInfo: Bidder | null;
  updateSaleAccount: (newSaleAccount: PublicKey | null) => void;
} {
  const [saleAccount, updateSaleAccount] = useState<PublicKey | null>(initialSaleAccount);
  const [bidderInfo, setBidderInfo] = useState<Bidder | null>(null);
  const walletInfo = useAppSelector(selectWalletInfo);
  const connection = new Connection(process.env.REACT_APP_SOLANA_NETWORK, {
    wsEndpoint: process.env.REACT_APP_SOLANA_NETWORK_WS,
  });

  useEffect(() => {
    if (!saleAccount || !walletInfo.walletAddress) {
      setBidderInfo(null);
      return;
    }

    // Get the user wallet and bidder account
    const userWalletAccount = new PublicKey(walletInfo.walletAddress);
    const userBidderAccount = findBidder(saleAccount, userWalletAccount)[0];

    // Fetch the bidder account where his global bid info is stored
    const fetchBidderInfo = async () => {
      const bidderInfo = await useGetBidderInfo(connection, saleAccount, userWalletAccount);
      setBidderInfo(bidderInfo);
    };

    // Init the bidder info
    fetchBidderInfo();

    // Subscribe to the sale
    const subscriptionId = connection.onAccountChange(userBidderAccount, async () => {
      console.debug("Bidder info account changed | Updating bidder info ...");
      fetchBidderInfo();
    });

    // Unsubscribe from the sale when the component unmounts
    return () => {
      connection.removeAccountChangeListener(subscriptionId);
    };
  }, [saleAccount, walletInfo]);

  return { bidderInfo, updateSaleAccount };
}

/*
 * Hook to subscribe to a outbid sale.
 * @param initialSaleAccount - The sale account to subscribe to
 * @returns selectedSale - The sale info
 * @returns setSaleAccount - Function to set the sale account
 */
export function useSubscribeToSale(initialSaleAccount: PublicKey | null) {
  // PERF: expose this in a context to avoid re-querying the same sale if multiple hook instances are used
  const [saleAccount, setSaleAccount] = useState<PublicKey | null>(initialSaleAccount);
  const [selectedSale, setSale] = useState<Sale | null>(null);
  const connection = new Connection(process.env.REACT_APP_SOLANA_NETWORK, {
    wsEndpoint: process.env.REACT_APP_SOLANA_NETWORK_WS,
  });

  // Update functions
  const fetchSale = async () => {
    try {
      const sale = await getSale(connection, saleAccount);
      setSale(sale);
    } catch (error) {
      setSale(null);
      console.error("[ERROR] Failed to fetch sale", error);
    }
  };

  // Subscribe to the sale account
  useEffect(() => {
    if (!saleAccount) {
      setSale(null);
      return;
    }

    // Init the sale
    fetchSale();

    // Subscribe to the sale
    const clientSubscriptionId = connection.onAccountChange(saleAccount, async () => {
      console.debug("Sale account changed | Updating sale ...");
      fetchSale();
    });

    const [specificIncentivesAccount] = findSpecificBidsIncentives(saleAccount);
    const clientSubscriptionIdSpecific = connection.onAccountChange(specificIncentivesAccount, async () => {
      console.debug("Specific incentives account changed | Updating specific incentives ...");
      fetchSale();
    });
    const [soldOutIncentivesAccount] = findSoldOutBidderIncentives(saleAccount);
    const clientSubscriptionIdSoldOut = connection.onAccountChange(soldOutIncentivesAccount, async () => {
      console.debug("Sold out incentives account changed | Updating sold out incentives ...");
      fetchSale();
    });

    // Unsubscribe from the sale when the component unmounts
    return () => {
      connection.removeAccountChangeListener(clientSubscriptionId);
      connection.removeAccountChangeListener(clientSubscriptionIdSpecific);
      connection.removeAccountChangeListener(clientSubscriptionIdSoldOut);
    };
  }, [saleAccount]);

  // Function to update saleAccount
  const updateSaleAccount = (newSaleAccount: PublicKey) => {
    setSaleAccount(newSaleAccount);
  };

  return { selectedSale, updateSaleAccount };
}
